/**
 * ActionParamForms - Formulários guiados dos parâmetros de cada ação
 *
 * Substitui o antigo campo "Parâmetros (JSON)" por campos reais, montados a
 * partir do contrato declarado no catálogo (`workflow_action_types.params_contract`):
 *   - `type: text | textarea | number`  -> input correspondente
 *   - `type: select` + `options`        -> seletor de lista fixa
 *   - `source: crm_sellers | ...`       -> seletor com dados reais do tenant
 * O payload gravado continua sendo o mesmo objeto `params` esperado pelo motor.
 * O bloco "Avançado (JSON)" fica oculto enquanto houver campos guiados no
 * contrato; só é exibido para ações sem contrato declarado.
 *
 * @version 1.4.0
 * RELEASE: 2026-10-17 v1.4.0 - Ação "Criar ordem de serviço": fontes service_types,
 *          service_order_phases e service_providers e rótulos das listas fixas.
 * RELEASE: 2026-10-02 v1.3.1 - Entidade Veículo: fontes vehicle_statuses,
 *          checklist_templates e distribution_queues e rótulos das listas fixas
 *          (tipo de responsável, alvo e sim/não).
 * RELEASE: 2026-09-06 v1.2.1 - Oculta o bloco "Avançado (JSON)" quando o
 *          contrato já possui campos guiados, mantendo-o apenas para ações
 *          sem contrato declarado.
 * RELEASE: 2026-09-06 v1.2.0 - Novo tipo de campo `variable_map`: permite mapear
 *          variáveis extras (nome -> campo do registro ou texto fixo) enviadas
 *          ao fluxo pela ação "Disparar fluxo".
 * RELEASE: 2026-08-31 v1.1.0 - Fase do CRM em cascata (workspace → pipeline → fase)
 *          e seleção visual de usuários (única ou múltipla).
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (designer BPMN)
 */


import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useWorkflowParamSources, type WorkflowParamOption } from '@/hooks/useWorkflowParamSources';
import type { WsWithPipelines } from '@/hooks/useTenantWorkspacesPipelines';
import { UserMultiSelect } from './UserMultiSelect';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { camposDaEntidade } from './camposDoNegocio';
import { useWorkflowServiceOrderSources } from '@/hooks/useWorkflowServiceOrderSources';


/** Campo declarado no contrato do catálogo */
interface ParamContractField {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  source?: string;
  options?: string[];
  /** Permite múltiplos valores (usado hoje pelos destinatários de notificação) */
  multiple?: boolean;
}

interface ActionParamFormsProps {
  /** Contrato da ação escolhida (array vindo do catálogo) */
  contrato: unknown;
  params: Record<string, unknown>;
  onChange: (params: Record<string, unknown>) => void;
  /** Entidade-raiz do processo (define os campos oferecidos nas variáveis extras) */
  entidade?: string | null;
}


/** Rótulos amigáveis para valores de listas fixas do catálogo */
const ROTULOS_FIXOS: Record<string, string> = {
  note: 'Anotação',
  call: 'Ligação',
  email: 'E-mail',
  meeting: 'Reunião',
  task: 'Tarefa',
  whatsapp: 'WhatsApp',
  visit: 'Visita',
  value: 'Valor do negócio',
  priority: 'Prioridade',
  expected_close_date: 'Previsão de fechamento',
  // v1.3.1: ações de Veículo/Vistoria
  user: 'Usuário',
  queue: 'Fila global',
  owner: 'Proprietário',
  all: 'Todos os veículos',
  first: 'Somente o primeiro',
  true: 'Sim',
  false: 'Não',
  // v1.4.0: ação "Criar ordem de serviço"
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
  per_deal: 'Uma por negócio',
  per_vehicle: 'Uma por veículo do negócio',
  per_service: 'Uma por serviço do negócio',
  seller: 'Vendedor do negócio',
  deal_owner: 'Responsável pelo negócio',
  none: 'Nenhum',
  days_after: 'Agendar após N dias',
  customer: 'Endereço do cliente',
};

export function ActionParamForms({ contrato, params, onChange, entidade }: ActionParamFormsProps) {
  // v1.3.0: as listas de tarefa respeitam o módulo do processo (Tarefa,
  // Retenção ou Jurídico) — estruturas separadas não podem se misturar.
  const fontes = useWorkflowParamSources(entidade);
  // v1.4.0: fontes da ação "Criar ordem de serviço" (só em processos de Negócio)
  const fontesOs = useWorkflowServiceOrderSources(entidade);

  const campos = useMemo<ParamContractField[]>(
    () => (Array.isArray(contrato) ? (contrato as ParamContractField[]).filter((c) => c && c.key) : []),
    [contrato],
  );

  const setValor = (chave: string, valor: unknown) => onChange({ ...params, [chave]: valor });

  /** Lista de opções para um campo com `source` declarado */
  const opcoesDaFonte = (source?: string): WorkflowParamOption[] => {
    switch (source) {
      case 'crm_sellers':
        return fontes.crm_sellers;
      case 'crm_pipeline_phases':
        return fontes.crm_pipeline_phases;
      case 'tenant_members':
        return fontes.tenant_members;
      case 'kanban_categories':
        return fontes.kanban_categories;
      case 'kanban_phases':
        return fontes.kanban_phases;
      // v1.1.0: motivos de conclusão (Retenção/Jurídico) da ação task.complete
      case 'task_completion_reasons':
        return fontes.task_completion_reasons;
      case 'flows':
        return fontes.flows;
      // v1.3.1: Veículos / Checklist
      case 'vehicle_statuses':
        return fontes.vehicle_statuses;
      case 'checklist_templates':
        return fontes.checklist_templates;
      case 'distribution_queues':
        return fontes.distribution_queues;
      // v1.4.0: Ordem de Serviço
      case 'service_types':
        return fontesOs.service_types;
      case 'service_order_phases':
        return fontesOs.service_order_phases;
      case 'service_providers':
        return fontesOs.service_providers;
      default:
        return [];
    }
  };

  // Ação sem contrato declarado: mantém o modo avançado como única saída
  if (campos.length === 0) {
    return <ModoAvancado params={params} onChange={onChange} descricao="Esta ação não exige parâmetros." />;
  }

  return (
    <div className="space-y-3">
      {campos.map((campo) => {
        const valor = params?.[campo.key];
        const rotulo = campo.label || campo.key;

        // v1.2.0: mapa de variáveis extras enviadas ao fluxo
        if (campo.type === 'variable_map') {
          return (
            <MapaDeVariaveis
              key={campo.key}
              rotulo={rotulo}
              entidade={entidade}
              valor={(valor && typeof valor === 'object' && !Array.isArray(valor)
                ? (valor as Record<string, string>)
                : {})}
              onChange={(mapa) => setValor(campo.key, mapa)}
            />
          );
        }



        // v1.1.0: fase do CRM em cascata (workspace → pipeline → fase)
        if (campo.source === 'crm_pipeline_phases') {
          return (
            <FaseEmCascata
              key={campo.key}
              rotulo={rotulo}
              obrigatorio={!!campo.required}
              hierarquia={fontes.crm_hierarquia}
              phaseId={valor ? String(valor) : ''}
              onChange={(faseId) => setValor(campo.key, faseId)}
            />
          );
        }

        // v1.1.0: usuários com seletor visual (único ou múltiplo)
        if (campo.source === 'tenant_members') {
          const multiplo = campo.type === 'uuid[]' || campo.multiple === true;
          const selecionados = Array.isArray(valor)
            ? (valor as string[])
            : valor
              ? [String(valor)]
              : [];

          return (
            <div key={campo.key} className="space-y-1.5">
              <Label className="text-xs">
                {rotulo} {campo.required && <span className="text-destructive">*</span>}
              </Label>
              <UserMultiSelect
                value={selecionados}
                multiple={multiplo}
                onChange={(ids) => setValor(campo.key, multiplo ? ids : (ids[0] ?? ''))}
                placeholder={`Selecione ${rotulo.toLowerCase()}`}
              />
            </div>
          );
        }

        // Seletor alimentado por dados reais do tenant
        if (campo.source) {
          const opcoes = opcoesDaFonte(campo.source);
          const grupos = agrupar(opcoes);

          return (
            <div key={campo.key} className="space-y-1.5">
              <Label className="text-xs">
                {rotulo} {campo.required && <span className="text-destructive">*</span>}
              </Label>
              <Select value={String(valor ?? '')} onValueChange={(v) => setValor(campo.key, v)}>
                <SelectTrigger>
                  <SelectValue placeholder={fontes.isLoading ? 'Carregando...' : `Selecione ${rotulo.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {grupos.map((grupo) => (
                    <SelectGroup key={grupo.nome}>
                      {grupo.nome !== '__sem_grupo__' && <SelectLabel>{grupo.nome}</SelectLabel>}
                      {grupo.itens.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                  {opcoes.length === 0 && (
                    <div className="px-2 py-3 text-xs text-muted-foreground">
                      {campo.source === 'flows' ? 'Nenhum fluxo publicado' : 'Nenhuma opção disponível'}
                    </div>
                  )}
                </SelectContent>
              </Select>
              {/* Explica o funcionamento real da ação "Disparar fluxo" */}
              {campo.source === 'flows' && (
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Só aparecem fluxos publicados. O fluxo é iniciado por este processo (não depende do gatilho
                  configurado dentro dele) e recebe os dados do registro. O mesmo registro não dispara o mesmo
                  fluxo duas vezes.
                </p>
              )}
            </div>

          );
        }


        // Lista fixa declarada no catálogo
        if (campo.type === 'select' && Array.isArray(campo.options)) {
          return (
            <div key={campo.key} className="space-y-1.5">
              <Label className="text-xs">
                {rotulo} {campo.required && <span className="text-destructive">*</span>}
              </Label>
              <Select value={String(valor ?? '')} onValueChange={(v) => setValor(campo.key, v)}>
                <SelectTrigger><SelectValue placeholder={`Selecione ${rotulo.toLowerCase()}`} /></SelectTrigger>
                <SelectContent>
                  {campo.options.map((op) => (
                    <SelectItem key={op} value={op}>
                      {ROTULOS_FIXOS[op] ?? op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        if (campo.type === 'textarea') {
          return (
            <div key={campo.key} className="space-y-1.5">
              <Label className="text-xs">
                {rotulo} {campo.required && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                rows={3}
                maxLength={2000}
                value={String(valor ?? '')}
                onChange={(e) => setValor(campo.key, e.target.value.slice(0, 2000))}
              />
            </div>
          );
        }

        if (campo.type === 'number') {
          return (
            <div key={campo.key} className="space-y-1.5">
              <Label className="text-xs">
                {rotulo} {campo.required && <span className="text-destructive">*</span>}
              </Label>
              <Input
                type="number"
                value={valor === undefined || valor === null || valor === '' ? '' : Number(valor)}
                onChange={(e) => setValor(campo.key, e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          );
        }

        // Texto simples (limite defensivo contra injeção de payloads gigantes)
        return (
          <div key={campo.key} className="space-y-1.5">
            <Label className="text-xs">
              {rotulo} {campo.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
              maxLength={255}
              value={String(valor ?? '')}
              onChange={(e) => setValor(campo.key, e.target.value.slice(0, 255))}
            />
          </div>
        );
      })}
    </div>
  );
}


/**
 * FaseEmCascata - Seleção encadeada Workspace → Pipeline → Fase
 *
 * O valor gravado no `params` continua sendo APENAS o `phase_id` esperado pelo
 * motor; workspace e pipeline são estados de navegação, derivados da fase atual.
 */
function FaseEmCascata({
  rotulo,
  obrigatorio,
  hierarquia,
  phaseId,
  onChange,
}: {
  rotulo: string;
  obrigatorio: boolean;
  hierarquia: WsWithPipelines[];
  phaseId: string;
  onChange: (faseId: string) => void;
}) {
  // Descobre workspace/pipeline a partir da fase já salva (edição de uma ação)
  const atual = useMemo(() => {
    for (const ws of hierarquia) {
      for (const pipe of ws.pipelines) {
        if (pipe.phases.some((f) => f.id === phaseId)) {
          return { workspaceId: ws.id, pipelineId: pipe.id };
        }
      }
    }
    return { workspaceId: '', pipelineId: '' };
  }, [hierarquia, phaseId]);

  const [workspaceId, setWorkspaceId] = useState(atual.workspaceId);
  const [pipelineId, setPipelineId] = useState(atual.pipelineId);

  // Mantém a navegação sincronizada quando a fase muda por fora
  useEffect(() => {
    if (atual.workspaceId) setWorkspaceId(atual.workspaceId);
    if (atual.pipelineId) setPipelineId(atual.pipelineId);
  }, [atual.workspaceId, atual.pipelineId]);

  const pipelines = hierarquia.find((w) => w.id === workspaceId)?.pipelines ?? [];
  const fases = pipelines.find((p) => p.id === pipelineId)?.phases ?? [];

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label className="text-xs">
        {rotulo} {obrigatorio && <span className="text-destructive">*</span>}
      </Label>

      <div className="space-y-1.5">
        <span className="text-[11px] text-muted-foreground">Workspace</span>
        <Select
          value={workspaceId}
          onValueChange={(v) => {
            // Trocar o nível superior limpa os níveis dependentes
            setWorkspaceId(v);
            setPipelineId('');
            onChange('');
          }}
        >
          <SelectTrigger><SelectValue placeholder="Selecione a workspace" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {hierarquia.map((ws) => (
              <SelectItem key={ws.id} value={ws.id}>{ws.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <span className="text-[11px] text-muted-foreground">Pipeline</span>
        <Select
          value={pipelineId}
          disabled={!workspaceId}
          onValueChange={(v) => {
            setPipelineId(v);
            onChange('');
          }}
        >
          <SelectTrigger><SelectValue placeholder="Selecione o pipeline" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {pipelines.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <span className="text-[11px] text-muted-foreground">Fase</span>
        <Select value={phaseId} disabled={!pipelineId} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder="Selecione a fase" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {fases.map((f) => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/**
 * MapaDeVariaveis - Variáveis extras enviadas ao fluxo
 *
 * Cada linha é um par "nome da variável" -> "campo do registro" (ou texto fixo).
 * O valor gravado em `params` é um objeto simples ({nome: origem}), resolvido no
 * banco pelo adapter `workflow_adapter_flow_trigger` com os dados reais do
 * registro. Dentro do fluxo, a variável é usada como {{nome}}.
 */
function MapaDeVariaveis({
  rotulo,
  entidade,
  valor,
  onChange,
}: {
  rotulo: string;
  entidade?: string | null;
  valor: Record<string, string>;
  onChange: (mapa: Record<string, string>) => void;
}) {
  const campos = useMemo(() => camposDaEntidade(entidade), [entidade]);
  const linhas = Object.entries(valor);

  /** Normaliza o nome da variável para um token válido dentro do fluxo */
  const normalizarNome = (texto: string) =>
    texto
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .slice(0, 40);

  /** Reconstrói o objeto preservando a ordem das linhas */
  const atualizar = (indice: number, nome: string, origem: string) => {
    const novo: Record<string, string> = {};
    linhas.forEach(([n, o], i) => {
      const chave = i === indice ? nome : n;
      if (chave) novo[chave] = i === indice ? origem : o;
    });
    onChange(novo);
  };

  const remover = (indice: number) => {
    const novo: Record<string, string> = {};
    linhas.forEach(([n, o], i) => {
      if (i !== indice) novo[n] = o;
    });
    onChange(novo);
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{rotulo}</Label>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => onChange({ ...valor, [`variavel_${linhas.length + 1}`]: '' })}
        >
          <Plus className="h-3 w-3" /> Variável
        </Button>
      </div>

      {linhas.length === 0 && (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Além dos dados do registro, você pode enviar variáveis com nomes próprios para usar dentro do fluxo.
        </p>
      )}

      {linhas.map(([nome, origem], indice) => {
        const ehCampo = campos.some((c) => c.campo === origem);
        return (
          <div key={indice} className="space-y-1.5 rounded-md bg-muted/30 p-2">
            <div className="flex items-center gap-2">
              <Input
                className="h-8 text-xs"
                maxLength={40}
                placeholder="nome_da_variavel"
                value={nome}
                onChange={(e) => atualizar(indice, normalizarNome(e.target.value), origem)}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-destructive"
                onClick={() => remover(indice)}
                aria-label="Remover variável"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <Select
              value={ehCampo ? origem : '__fixo__'}
              onValueChange={(v) => atualizar(indice, nome, v === '__fixo__' ? '' : v)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Escolha o conteúdo" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__fixo__">Texto fixo</SelectItem>
                {campos.map((c) => (
                  <SelectItem key={c.campo} value={c.campo}>
                    {c.grupo} — {c.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!ehCampo && (
              <Input
                className="h-8 text-xs"
                maxLength={255}
                placeholder="Texto que será enviado"
                value={origem}
                onChange={(e) => atualizar(indice, nome, e.target.value.slice(0, 255))}
              />
            )}

            {nome && (
              <p className="text-[11px] text-muted-foreground">
                No fluxo, use <code>{`{{${nome}}}`}</code>.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Agrupa opções pelo campo `grupo`, preservando a ordem original */

function agrupar(opcoes: WorkflowParamOption[]): Array<{ nome: string; itens: WorkflowParamOption[] }> {
  const mapa = new Map<string, WorkflowParamOption[]>();
  for (const op of opcoes) {
    const nome = op.grupo || '__sem_grupo__';
    mapa.set(nome, [...(mapa.get(nome) ?? []), op]);
  }
  return Array.from(mapa.entries()).map(([nome, itens]) => ({ nome, itens }));
}

/** Bloco "Avançado (JSON)", fechado por padrão — só para casos excepcionais */
function ModoAvancado({
  params,
  onChange,
  descricao,
}: {
  params: Record<string, unknown>;
  onChange: (p: Record<string, unknown>) => void;
  descricao?: string;
}) {
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="json" className="border-none">
        <AccordionTrigger className="py-1 text-xs text-muted-foreground hover:no-underline">
          Avançado (JSON)
        </AccordionTrigger>
        <AccordionContent>
          {descricao && <p className="mb-2 text-xs text-muted-foreground">{descricao}</p>}
          <Textarea
            rows={4}
            className="font-mono text-xs"
            defaultValue={JSON.stringify(params ?? {}, null, 2)}
            onBlur={(e) => {
              try {
                const parsed = JSON.parse(e.target.value || '{}');
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) onChange(parsed);
              } catch {
                // JSON inválido: mantém o valor anterior sem quebrar a tela
              }
            }}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
